import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, NavLink as RouterNavLink, useSearchParams } from 'react-router-dom';
import { Image } from 'semantic-ui-react';
import { Menu } from '../../layout/Menu';
import { device } from '../../../theme';
import { ParticipantChart } from './ParticipantChart';
import { ClubChart } from './ClubChart';

// Import statistics data
let statisticsData = {};
try {
  statisticsData = require('../../../data/statistics.json');
} catch (e) {
  console.warn('Statistics data not yet generated. Run: npx ts-node bin/generate-statistics.ts');
}

export const Statistics = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedCompetition, setSelectedCompetition] = useState('');

  const competitions = useMemo(() => {
    return Object.keys(statisticsData)
      .filter(key => statisticsData[key].length > 0)
      .sort((a, b) => a.localeCompare(b, 'nb')); // Sort alphabetically with Norwegian locale
  }, []);

  const selectedData = useMemo(() => {
    if (!selectedCompetition || !statisticsData[selectedCompetition]) {
      return [];
    }
    return statisticsData[selectedCompetition];
  }, [selectedCompetition]);

  // Initialize from URL or set default
  useEffect(() => {
    const urlComp = searchParams.get('competition');
    if (urlComp && competitions.includes(urlComp)) {
      setSelectedCompetition(urlComp);
    } else if (!selectedCompetition && competitions.length > 0) {
      // Prefer 'NM' if available, otherwise use first competition
      const defaultComp = competitions.includes('NM') ? 'NM' : competitions[0];
      setSelectedCompetition(defaultComp);
      setSearchParams({ competition: defaultComp });
    }
  }, [competitions]);

  // Update URL when competition changes
  const handleCompetitionChange = (comp) => {
    setSelectedCompetition(comp);
    setSearchParams({ competition: comp });
  };

  return (
    <>
      <Header>
        <div>
          <a href="https://nsff.no">
            <Image src={"/resultater/images/logo-darkgreen.png"} alt="Logo" height={"60"} />
          </a>
        </div>
        <div>
          <h1>Statistikk</h1>
        </div>
        <div>
          <Link to={"/"}>Tilbake</Link>
        </div>
      </Header>
      <Menu />
      <Container>
        <ContentWrapper>
          <Title>Konkurransestatistikk</Title>

          <NavLinks>
            <NavLink to={`/statistikk${selectedCompetition ? `?competition=${selectedCompetition}` : ''}`} end>Konkurranser</NavLink>
            <NavLink to={`/statistikk/personer${selectedCompetition ? `?competition=${selectedCompetition}` : ''}`}>Personer</NavLink>
            <NavLink to={`/statistikk/klubber${selectedCompetition ? `?competition=${selectedCompetition}` : ''}`}>Klubber</NavLink>
            <NavLink to="/statistikk/hederstegn">Hederstegn</NavLink>
            <NavLink to="/statistikk/nsff">NSFF</NavLink>
          </NavLinks>

          <CompetitionSelector>
            <label htmlFor="competition-select">Velg konkurranse: </label>
            <select
              id="competition-select"
              value={selectedCompetition}
              onChange={(e) => handleCompetitionChange(e.target.value)}
            >
              {competitions.map(comp => (
                <option key={comp} value={comp}>{comp}</option>
              ))}
            </select>
          </CompetitionSelector>

          {selectedData.length > 0 && (
            <>
              <ChartSection>
                <ChartTitle>Antall deltakere per konkurranse</ChartTitle>
                <ParticipantChart data={selectedData} />
              </ChartSection>

              <ChartSection>
                <ChartTitle>Antall klubber per konkurranse</ChartTitle>
                <ClubChart data={selectedData} />
              </ChartSection>

              <TableWrapper>
                <StatsTable>
                  <thead>
                    <tr>
                      <th>År</th>
                      <th>Del</th>
                      <th>Deltakere</th>
                      <th>Klubber</th>
                      <th>Innleveringer</th>
                      <th>Maks mulig bilder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedData.map((stat, idx) => (
                      <tr key={idx}>
                        <td>{stat.year}</td>
                        <td>{stat.part || '-'}</td>
                        <td>
                          {stat.participantCount}
                          {!stat.hasCompleteData && <AwardedOnly title="Kun deltakere med premier">*</AwardedOnly>}
                        </td>
                        <td>
                          {stat.clubCount}
                          {!stat.hasCompleteData && <AwardedOnly title="Kun klubber med premier">*</AwardedOnly>}
                        </td>
                        <td>
                          {stat.submissionCount}
                          {!stat.hasCompleteData && <AwardedOnly title="Kun premierte bilder">*</AwardedOnly>}
                        </td>
                        <td>{stat.maxPossibleImages || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </StatsTable>
              </TableWrapper>
            </>
          )}

          {selectedData.length === 0 && (
            <NoData>Ingen statistikk tilgjengelig for denne konkurransen</NoData>
          )}

          {selectedData.length > 0 && selectedData.some(s => !s.hasCompleteData) && (
            <FootNote>* Kun data for premierte bilder/deltakere (komplett innleveringsdata ikke tilgjengelig)</FootNote>
          )}
        </ContentWrapper>
      </Container>
    </>
  );
};

const Container = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 20px;
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  background-color: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  height: 70px;
  background-color: #90b361;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #36501c;
  font-family: 'Fibon Neue';

  > div {
    display: flex;
    flex-direction: row;
    margin-left: 10px;

    > div {
      margin-left: 20px;
    }

    > a, > h1 {
      color: ${(p) => p.theme.color.primary.base};
    }

    align-items: center;
  }

  > div:nth-child(2) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-size: 1.4rem;

    h1 {
      margin: 0;
      font-size: 1.4rem;
      font-weight: normal;
    }
  }

  > div:nth-child(3) {
    margin-right: 20px;
    > a {
      color: ${(p) => p.theme.color.primary.base};
    }
  }
`;

const Title = styled.h1`
  color: #36501c;
  font-family: 'Fibon Neue';
  margin-bottom: 30px;
  text-align: center;
`;

const CompetitionSelector = styled.div`
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: center;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: stretch;
  }

  label {
    font-weight: bold;
    color: #36501c;
  }

  select {
    padding: 8px 15px;
    border: 2px solid #90b361;
    border-radius: 4px;
    font-size: 16px;
    background-color: white;
    color: #36501c;
    cursor: pointer;
    min-width: 200px;

    &:focus {
      outline: none;
      border-color: #36501c;
    }

    @media ${device.mobile} {
      min-width: 100%;
    }
  }
`;

const ChartSection = styled.div`
  margin-bottom: 50px;
`;

const ChartTitle = styled.h2`
  color: #36501c;
  font-family: 'Fibon Neue';
  margin-bottom: 20px;
  font-size: 1.3rem;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-top: 30px;
  -webkit-overflow-scrolling: touch;

  @media ${device.mobile} {
    margin-left: -30px;
    margin-right: -30px;
    padding: 0 15px;
  }
`;

const StatsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 600px;

  th, td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
    white-space: nowrap;
  }

  th {
    background-color: #90b361;
    color: #36501c;
    font-weight: bold;
  }

  tbody tr:hover {
    background-color: #f9f9f9;
  }

  @media ${device.mobile} {
    font-size: 12px;

    th, td {
      padding: 8px 6px;
    }
  }
`;

const NoData = styled.div`
  text-align: center;
  padding: 50px;
  color: #666;
  font-size: 1.2rem;
`;

const NavLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
`;

const NavLink = styled(RouterNavLink)`
  padding: 10px 20px;
  border: 2px solid #90b361;
  border-radius: 4px;
  text-decoration: none;
  color: #36501c;
  font-weight: bold;
  transition: all 0.2s;

  &:hover {
    background-color: #90b361;
    color: white;
  }

  &.active {
    background-color: #36501c;
    border-color: #36501c;
    color: white;
  }
`;

const AwardedOnly = styled.span`
  color: #90b361;
  font-weight: bold;
  margin-left: 3px;
  cursor: help;
`;

const FootNote = styled.div`
  margin-top: 20px;
  padding: 15px;
  background-color: #f9f9f9;
  border-left: 4px solid #90b361;
  color: #666;
  font-size: 0.9rem;
  font-style: italic;
`;
